import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
} from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import {
    AppStateFacade,
    Setting,
} from '@app-state';
import { UiModalComponent } from '@components/ui-modal/ui-modal.component';
import {
    IPasswordComplexity,
    ITenant,
    ModalResultType,
} from '@models';
import { TenantService } from '@services';
import UiValidators, {
    UI_EMAIL_MAX_LENGTH,
    UI_NAME_LONG_MAX_LENGTH,
    UI_NAME_MAX_LENGTH,
    UI_PASSWORD_MAX_LENGTH,
} from '@validators';

import cloneDeep from 'lodash-es/cloneDeep';
import { BehaviorSubject } from 'rxjs';
import { finalize } from 'rxjs/operators';

const CONNECTION_STRING_CONTROL_KEY = 'connectionString';

@Component({
    templateUrl: './tenant.modal.html',
    providers: [TenantService],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TenantModalComponent
    extends UiModalComponent<ITenant>
    implements OnInit {
    public form!: FormGroup;
    public loading$ = new BehaviorSubject(false);
    public passwordComplexity?: IPasswordComplexity;
    public isMultipleDatabasesEnabled?: boolean;

    private _tenantService = this._injector.get<TenantService>(TenantService);
    private _appState = this._injector.get<AppStateFacade>(AppStateFacade);
    private _connectionStringControl = new FormControl('', UiValidators.required());

    ngOnInit() {
        if (!this.context.isNewEntry) {
            this._dialogRef.close();
            return;
        }

        this.passwordComplexity = this._appState.settings.passwordComplexity;
        this.isMultipleDatabasesEnabled = this._appState.settings.get(Setting.isMultipleDatabasesEnabled);

        const fb = this._injector.get<FormBuilder>(FormBuilder);
        this.form = fb.group({
            'name': [
                '',
                Validators.compose([
                    Validators.maxLength(UI_NAME_LONG_MAX_LENGTH),
                    UiValidators.required(),
                    UiValidators.regex('^[\\p{L}][\\p{L}0-9-_]+$'),
                ]),
            ],
            'adminEmailAddress': [
                '',
                Validators.compose([
                    Validators.maxLength(UI_EMAIL_MAX_LENGTH),
                    UiValidators.email(true),
                ]),
            ],
            'adminName': [
                '',
                Validators.compose([
                    Validators.maxLength(UI_NAME_MAX_LENGTH),
                ]),
            ],
            'adminSurname': [
                '',
                Validators.compose([
                    Validators.maxLength(UI_NAME_MAX_LENGTH),
                ]),
            ],
            'adminPassword': [
                '',
                Validators.compose([
                    Validators.maxLength(UI_PASSWORD_MAX_LENGTH),
                    UiValidators.complexity(this.passwordComplexity!),
                ]),
            ],
            'adminPasswordConfirmation': [
                '',
                Validators.compose([
                    Validators.maxLength(UI_PASSWORD_MAX_LENGTH),
                ]),
            ],
            'hasConnectionString': [
                false,
            ],
        }, {
                validator: UiValidators.confirmPassword('adminPassword', 'adminPasswordConfirmation'),
            });
    }

    public onSubmit() {
        if (
            !this.form.valid ||
            this.loading$.value
        ) {
            return;
        }

        this.loading$.next(true);
        const payloadPatch = cloneDeep(this.form.value);
        delete payloadPatch['adminPasswordConfirmation'];
        Object.assign(this.context.data, payloadPatch);

        if (this.context.isNewEntry) {
            this._tenantService.add(this.context.data)
                .pipe(
                    finalize(
                        () => this.loading$.next(false),
                    ),
                )
                .subscribe(
                    () => {
                        this._dialogRef.close({
                            type: ModalResultType.Add,
                        });
                    });
        } else {
            const clone = cloneDeep(this.context.data);
            this._tenantService.update(this.context.data.id, clone)
                .pipe(
                    finalize(
                        () => this.loading$.next(false),
                    ),
                )
                .subscribe(
                    () => {
                        this._dialogRef.close({
                            type: ModalResultType.Update,
                            data: clone,
                        });
                    });
        }
    }

    public hasConnectionStringChanged(event: MatSlideToggleChange) {
        if (event.checked) {
            this.form.addControl(CONNECTION_STRING_CONTROL_KEY, this._connectionStringControl);
        } else {
            this.form.removeControl(CONNECTION_STRING_CONTROL_KEY);
        }
    }
}
